import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Avatar,
    Chip,
    Skeleton,
    Button,
    Alert,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useTheme } from '@mui/material/styles';
import { updateHouse } from '../../../api_calls/api_calls_houses';
import { getInvoiceData, getPast30DaysCost } from '../../../api_calls/api_calls_invoices';

const PROVIDER_LOGO_PATH = '/images/providers/';

const ComparePlansModal = ({ open, onClose, houseId, currentPlanProviderName, currentPlanName, onPlanUpdate }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [error, setError] = useState(null);
    const [switchingPlan, setSwitchingPlan] = useState(null);
    const [switchError, setSwitchError] = useState(null);

    // Get current month in YYYY-MM format
    const getCurrentMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    useEffect(() => {
        if (open && houseId) {
            setLoading(true);
            setError(null);
            setSwitchError(null);
            setSwitchingPlan(null);

            const currentMonth = getCurrentMonth();

            // Step 1: Fetch all invoices
            getInvoiceData('green', currentMonth)
                .then(async (invoices) => {
                    const invoiceList = invoices || [];

                    // Step 2: Fetch cost for each invoice
                    const planPromises = invoiceList.map(async (invoice) => {
                        try {
                            const costData = await getPast30DaysCost(houseId, invoice.provider_name, invoice.name);
                            return {
                                ...invoice,
                                costData: costData,
                                monthlyCost: costData?.total_bill_amount || 0
                            };
                        } catch (error) {
                            console.error(`Failed to fetch cost for ${invoice.provider_name}/${invoice.name}:`, error);
                            return {
                                ...invoice,
                                costData: null,
                                monthlyCost: null
                            };
                        }
                    });

                    const plansWithCosts = await Promise.all(planPromises);

                    // Filter out plans without cost data and sort by monthly cost (cheapest first)
                    const validPlans = plansWithCosts
                        .filter(plan => plan.monthlyCost !== null)
                        .sort((a, b) => a.monthlyCost - b.monthlyCost);

                    setPlans(validPlans);
                    setLoading(false);
                })
                .catch((error) => {
                    setError(error.message || 'Failed to load plans');
                    setLoading(false);
                });
        } else if (!open) {
            setPlans([]);
            setError(null);
            setSwitchError(null);
            setSwitchingPlan(null);
        }
    }, [open, houseId]);

    const handleSwitchPlan = async (plan) => {
        setSwitchingPlan(plan);
        setSwitchError(null);

        const updateData = {
            invoiceprovidername: plan.provider_name,
            invoicename: plan.name
        };

        try {
            await updateHouse(houseId, updateData);
            if (onPlanUpdate) {
                onPlanUpdate();
            }
            onClose();
        } catch (error) {
            setSwitchError(error.message || 'Failed to switch plan');
        } finally {
            setSwitchingPlan(null);
        }
    };

    const getCurrentPlanMonthlyCost = () => {
        const currentPlan = plans.find(
            plan => plan.provider_name === currentPlanProviderName && plan.name === currentPlanName
        );
        return currentPlan?.monthlyCost || null;
    };

    const calculateSavings = (planMonthlyCost) => {
        const currentCost = getCurrentPlanMonthlyCost();
        if (!currentCost || !planMonthlyCost) return null;
        const savings = currentCost - planMonthlyCost;
        return savings > 0 ? savings : null;
    };

    const isCurrentPlan = (plan) => {
        return plan.provider_name === currentPlanProviderName && plan.name === currentPlanName;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '25px',
                    maxHeight: '90vh',
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: theme.palette.primary.light,
                            width: 40,
                            height: 40
                        }}
                    >
                        <CompareArrowsIcon sx={{ color: 'white' }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Compare Plans
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Based on your consumption in the last 30 days
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box>
                        <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
                        <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : plans.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No plans available for comparison.
                    </Typography>
                ) : (
                    <Box sx={{ maxHeight: '70vh', overflowY: 'auto', p: 1 }}>
                        {switchError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSwitchError(null)}>
                                {switchError}
                            </Alert>
                        )}
                        {plans.map((plan, index) => {
                            const current = isCurrentPlan(plan);
                            const savings = calculateSavings(plan.monthlyCost);
                            const rank = index + 1;

                            return (
                                <Box
                                    key={`${plan.provider_name}-${plan.name}`}
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        borderRadius: 3,
                                        backgroundColor: current ? theme.palette.primary.light + '15' : 'white',
                                        border: `1px solid ${current ? theme.palette.primary.main : theme.palette.grey[200]}`,
                                        boxShadow: current ? `0 2px 8px ${theme.palette.primary.light}40` : 'none',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                        {/* Provider Logo with Rank */}
                                        <Box sx={{ position: 'relative' }}>
                                            <Avatar
                                                src={PROVIDER_LOGO_PATH + plan.provider_name + '.png'}
                                                alt={plan.provider_name}
                                                sx={{
                                                    width: 56,
                                                    height: 56,
                                                    bgcolor: theme.palette.grey[100],
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -8,
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: '50%',
                                                    backgroundColor: rank === 1 ? theme.palette.success.main : theme.palette.warning.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px solid white',
                                                    boxShadow: 1,
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                                    {rank}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Plan Info */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {plan.provider_name}
                                                </Typography>
                                                {current && (
                                                    <Chip
                                                        label="Current Plan"
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                        }}
                                                    />
                                                )}
                                                {savings && savings > 0 && !current && (
                                                    <Chip
                                                        label={`Save €${savings.toFixed(2)} monthly`}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: theme.palette.success.main,
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            height: '20px',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                {plan.name}
                                            </Typography>

                                            {/* Rate and Flat Fee */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <BoltIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Rate{' '}
                                                        <Typography component="span" variant="body2" fontWeight={600}>
                                                            €{plan.day_price_high?.toFixed(4) || '-'}/kWh
                                                        </Typography>
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AttachMoneyIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Flat Fee{' '}
                                                        <Typography component="span" variant="body2" fontWeight={600}>
                                                            €{plan.flat_fee?.toFixed(2) || '-'}/month
                                                        </Typography>
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Monthly Cost */}
                                            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                                                €{plan.monthlyCost?.toFixed(2) || '-'} per month
                                            </Typography>

                                            {/* Switch Plan Button */}
                                            {!current && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleSwitchPlan(plan)}
                                                    disabled={switchingPlan !== null}
                                                    startIcon={switchingPlan === plan ? <CircularProgress size={16} /> : <SwapHorizIcon />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        borderRadius: 2,
                                                        fontWeight: 600,
                                                        mt: 1,
                                                    }}
                                                >
                                                    {switchingPlan === plan ? 'Switching...' : 'Switch Plan'}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ComparePlansModal;

